import { Fragment, useState } from "react";

import { Row } from "@components/Flex";
import { NoDataDisclaimer } from "@components/Goals/NoDataDisclaimer";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { NODE_COUNT } from "@constants";
import { useGoalDetailsBreakdown, useGoalsContext } from "@hooks";
import { i18n, Translations } from "@i18n";
import { formatPercentage } from "@utils";
import { sectorContent } from "./content";
import { SeeMoreButton } from "./SeeMoreButton";
import { useStyles } from "./styles";
import { GoalInvestmentBreakdownProps } from "./types";

const DEFAULT_LIST_SIZE = 5;

export const GoalInvestmentBreakdown = ({ style }: GoalInvestmentBreakdownProps) => {
  const styles = useStyles();

  const [displayFullList, setDisplayFullList] = useState(false);

  const { beneficiaryId, goalId } = useGoalsContext();

  const { data, loading, error } = useGoalDetailsBreakdown({
    variables: {
      beneficiaryId,
      goalId: goalId!,
      nodeCount: NODE_COUNT,
    },
    fetchPolicy: "cache-first",
  });

  const breakdown = data?.portfolioBreakdown;

  if (!breakdown || loading) return null;

  const sectorListSize = breakdown.sectorExposures?.length ?? 0;

  const condensedSize = Math.min(sectorListSize, DEFAULT_LIST_SIZE);
  const listSize = displayFullList ? sectorListSize : condensedSize;

  const shouldDisplaySeeMoreCTA = !displayFullList && sectorListSize !== condensedSize;

  const sectors = (() => {
    if (!breakdown.sectorExposures) return [];

    return [...breakdown.sectorExposures].sort((a, b) => {
      const aPercentage = a?.factorPercentage ?? 0;
      const bPercentage = b?.factorPercentage ?? 0;
      return bPercentage - aPercentage;
    });
  })().slice(0, listSize);

  const onSeeMoreClicked = () => {
    setDisplayFullList(true);
  };

  const content = (() => {
    if (!sectors?.length || error) {
      return (
        <>
          <NoDataDisclaimer />
          <Spacing size={SpacingSizes.S} />
        </>
      );
    }

    return (
      <>
        {sectors.map((sector) => {
          if (!sector) return null;

          const percentage = formatPercentage(sector.factorPercentage);

          return (
            <Fragment key={sector.name}>
              <Row>
                <Body style={styles.left}>{sectorContent[sector.name]}</Body>
                <Body>{percentage}</Body>
              </Row>
              <Spacing size={SpacingSizes.S} />
            </Fragment>
          );
        })}

        {shouldDisplaySeeMoreCTA ? (
          <div style={styles.seeMore}>
            <SeeMoreButton onClick={onSeeMoreClicked} />
          </div>
        ) : null}
      </>
    );
  })();

  return (
    <div style={{ ...styles.container, ...style }}>
      <Row>
        <Body style={{ ...styles.title, ...styles.left }}>
          {i18n.t(Translations.InvestmentBreakdownTitle)}
        </Body>
        <Body style={styles.title}>%</Body>
      </Row>
      <Spacing size={SpacingSizes.M} />

      {content}
    </div>
  );
};
